// WORKFORCE (Andrei, 2026-10-26): the Workforce mode has digital employees (DE: seat, role,
// goal, KPIs, budget, memory, runs — the `digitalEmployees.*` routes) and agents (AG: task
// executors with no seat, KPIs or budget — these routes). Every member sees every agent (the
// roster is a `viewer` read, like the DE roster: visibility never depends on depth); a manager,
// the person an agent reports to, or a person above the DE it reports to may change it (pause,
// resume, retire, rename, move team, reassign, take over). Agents are registered by ops
// (`infra/ops/register-agents.ts`) or, since WF-POLISH, added by hand (`workforceAgents.create`).
import { z } from "zod";
import { Run } from "../agent-rows";
import {
  AgentUseSource,
  ApprovalKind,
  WorkforceAgentReportsToKind,
  WorkforceAgentStatus,
} from "../enums";
import { MicrosAmount, Timestamp, Uuid } from "../primitives";
import { WorkforceAgent } from "../rows";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { AgentPurge, AgentPurgePlan, AgentPurgeResult, WorkerProfileFields } from "./org";
import { defineRoute } from "./route";

/** WF-POLISH: an agent's responsibilities, one short line each. */
const AgentResponsibilities = z.array(z.string().trim().min(1).max(500)).max(50);
/** HELPERS: the skills an agent runs, by key (a skill pack's key or a runtime skill's name). */
export const AgentSkills = z.array(z.string().trim().min(1).max(120)).max(50);

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

// ACTIVITY (Workforce › Activity, docs/design/2026-10-27-workforce-activity.md): what the digital
// workforce is doing, one row per run (never per event), live runs first, then the rest newest
// first; the run's trail is `runs.get` (steps) plus `events.list?actor_run_id=` (managers).
// Visibility is `runs.list`'s: a manager sees every run, anyone else the runs of the seats they
// hold or supervise. Nothing is written here; the row's controls are the existing routes.

/** Who: one DE (membership), one AG (the runs of the DE it reports to), or one team's seats. */
export const ActivityWhoKind = z.enum(["de", "ag", "team"]);
/**
 * State: `live` (queued, running, waiting on input, an approval or child runs), `waiting` (on a
 * person: an approval, input or review; and a lost run parked for a person), `done` (completed or
 * stopped), `failed`. No filter: everything.
 */
export const ActivityState = z.enum(["live", "waiting", "done", "failed"]);

/** `who_kind` and `who_id` go together (422 otherwise). */
export const ActivityQuery = PageQuery.extend({
  who_kind: ActivityWhoKind.optional(),
  who_id: Uuid.optional(),
  state: ActivityState.optional(),
  /** The child runs of one run (the row's "+n child runs"), any state, newest first. */
  parent_run_id: Uuid.optional(),
  /** The start of the viewer's day, for `facts` (default: the last 24 hours). */
  day_start: Timestamp.optional(),
});

/** The row's one "what is it doing" line, newest first of what applies (design §3.1). */
export const ActivityLastKind = z.enum([
  "checkpoint",
  "event",
  "approval",
  "summary",
  "reason",
  "step",
]);

export const ActivityRow = z
  .object({
    run: Run,
    /** The DE whose run it is ("DE · " label in `name`), its seat and its seat's first team. */
    who: z.object({
      membership_id: Uuid,
      name: z.string(),
      seat_id: Uuid.nullable(),
      seat_title: z.string().nullable(),
      team: z.object({ id: Uuid, name: z.string() }).nullable(),
    }),
    /** The run's task, when the caller may open it. */
    task: z.object({ id: Uuid, title: z.string(), board_id: Uuid }).nullable(),
    /** The run has a task the caller may not open (or that was deleted). */
    task_hidden: z.boolean(),
    last: z.object({ kind: ActivityLastKind, text: z.string(), at: Timestamp }).nullable(),
    /** The newest pending approval the run asked for. */
    waiting_approval: z.object({ id: Uuid, kind: ApprovalKind, title: z.string() }).nullable(),
    /** Child runs the caller may view (`parent_run_id` lists them). */
    child_runs: z.int().min(0),
  })
  .meta({ id: "ActivityRow" });

export const ActivityFeed = page(ActivityRow)
  .extend({
    /** Live runs, waiting-on-a-person first, then oldest start; the first page only. */
    now: z.array(ActivityRow),
    /** More live runs than `now` carries. */
    now_truncated: z.boolean(),
    /** The toolbar's fact ("7 runs today · 2 waiting · $1.20"), over the Who filter. */
    facts: z.object({
      runs_today: z.int().min(0),
      waiting: z.int().min(0),
      cost_micros_today: MicrosAmount,
      /**
       * WF-POLISH: the same runs' tokens (in + out). With the cost both 0 while runs exist,
       * nothing reported usage (self-hosted runs), which is not "free".
       */
      tokens_today: z.int().min(0),
    }),
  })
  .meta({ id: "ActivityFeed" });

// HOME-DASH (Andrei, 2026-10-27: "I would like the run time for agents summarized"): the
// digital workforce's runs over a window (Home's "Agent runtime" widget, a week). The same
// visibility as the Activity feed; every DE's runs, an AG's included (an AG runs as its DE).

/** `[from, to)`; both or neither (default: the 7 days up to now). At most 93 days. */
export const RuntimeQuery = z.object({
  from: Timestamp.optional(),
  to: Timestamp.optional(),
});

export const RuntimeSummary = z
  .object({
    from: Timestamp,
    to: Timestamp,
    /** Runs created in the window (child runs included). */
    runs: z.int().min(0),
    /** The digital employees those runs belong to. */
    des: z.int().min(0),
    /** Of those runs, still live. */
    live: z.int().min(0),
    /** Summed start → end (→ now for a live run), in milliseconds. */
    active_ms: z.int().min(0),
    tokens_in: z.int().min(0),
    tokens_out: z.int().min(0),
    cost_micros: MicrosAmount,
    /**
     * Runs that reported cost or tokens. An ended run with neither (the self-hosted runtime
     * today) is `unreported`: its usage is unknown, not zero.
     */
    metered: z.int().min(0),
    unreported: z.int().min(0),
  })
  .meta({ id: "RuntimeSummary" });

export const WorkforceAgentListQuery = PageQuery.extend({
  include_retired: z.enum(["true", "false"]).optional(),
});

export const WorkforceAgentSummary = WorkforceAgent.omit({
  actor_kind: true,
  actor_membership_id: true,
})
  .extend({
    team: z.object({ id: Uuid, name: z.string() }).nullable(),
    reports_to: z.object({
      membership_id: Uuid,
      kind: WorkforceAgentReportsToKind,
      /** The membership's display name (a DE's carries its "DE · " label). */
      name: z.string(),
    }),
    /** The caller may change it (manager, the person it reports to, or a person above). */
    can_edit: z.boolean(),
    /**
     * AG-MEMORY: where its Recall key stands on the satellite (the last recall-keys push):
     * `applied`, `pending`, `unknown_profile` (the panel knows no such profile), `no_profile`
     * (no stored runtime profile: nothing to push to), or `waiting` (no push has named it yet).
     */
    recall_keys: z
      .object({
        state: z.enum(["applied", "pending", "unknown_profile", "no_profile", "waiting"]),
        at: Timestamp.nullable(),
      })
      .optional(),
    /**
     * HELPERS-2: the other DEs that use it (it shows under each of them as well as under its
     * home, `reports_to`), by name, and how each came to: `history`, `manager` or `self`.
     */
    used_by: z.array(z.object({ membership_id: Uuid, name: z.string(), source: AgentUseSource })),
  })
  .meta({ id: "WorkforceAgentSummary" });

/** HELPERS-2: one DE's use of an agent (`workforceAgents.addUse` / `removeUse`). */
export const AgentUseParams = z.object({ co: Uuid, id: Uuid, membership_id: Uuid });

/**
 * HELPERS-2: a DE declares a helper it uses (or no longer uses), by the agent's slug. Its own
 * helpers (it leads them) need no declaring; retired agents cannot be declared.
 */
export const AgentUseDeclare = z
  .strictObject({
    agent: z
      .string()
      .max(64)
      .regex(/^[a-z0-9][a-z0-9-]{0,63}$/),
    /** Default true; false stops using it. */
    used: z.boolean().optional(),
  })
  .meta({ id: "AgentUseDeclare" });

export const AgentUseDeclared = z
  .object({
    agent_id: Uuid,
    slug: z.string(),
    name: z.string(),
    /** Whether the DE now uses it (its home DE always does). */
    used: z.boolean(),
    /** It reports to this DE: shown under it already, nothing was stored. */
    home: z.boolean(),
  })
  .meta({ id: "AgentUseDeclared" });

export const WorkforceAgentPatch = z
  .strictObject({
    name: z.string().trim().min(1).max(200).optional(),
    team_id: Uuid.nullable().optional(),
    status: WorkforceAgentStatus.optional(),
    /** Reassign (a DE or a person of the company; its chain must end at a person) or take over. */
    reports_to_membership_id: Uuid.optional(),
    /** WF-POLISH: what it does. */
    ...WorkerProfileFields,
    responsibilities: AgentResponsibilities.optional(),
    skills: AgentSkills.optional(),
  })
  .refine((p) => Object.keys(p).length > 0, "nothing to change")
  .meta({ id: "WorkforceAgentPatch" });

/**
 * WF-POLISH (Andrei, 2026-10-27: "there's no way to add agents"): add an agent by hand. The slug
 * is derived from the name when not given (unique in the company: `-2`, `-3`… on a clash). Who
 * may: a manager; or a person adding an agent that reports to them, or to a DE whose seat they
 * hold, supervise or stand above (the PATCH rule, applied to the new agent).
 */
export const WorkforceAgentCreate = z
  .strictObject({
    name: z.string().trim().min(1).max(200),
    slug: z
      .string()
      .max(64)
      .regex(/^[a-z0-9][a-z0-9-]{0,63}$/)
      .optional(),
    team_id: Uuid.nullable().optional(),
    reports_to_membership_id: Uuid,
    /** Where it runs, when known (a Hermes container and profile). */
    runtime: z
      .strictObject({
        container: z.string().trim().min(1).max(256),
        profile: z.string().trim().min(1).max(256),
        clawpanel_instance: z.string().trim().min(1).max(64).nullable().optional(),
      })
      .nullable()
      .optional(),
    /** DE-FOLLOWUPS: default `active` with a runtime, `not_deployed` without one. */
    status: WorkforceAgentStatus.extract(["active", "not_deployed"]).optional(),
    ...WorkerProfileFields,
    responsibilities: AgentResponsibilities.optional(),
    skills: AgentSkills.optional(),
  })
  .meta({ id: "WorkforceAgentCreate" });

/**
 * CONTENT-STUDIO-LINK (2026-10-29): a DE reports what one of ITS agents (AG) is doing — when it
 * hands the agent work and when the agent returns. The agent runs inside the DE's container (a
 * Hermes profile), has no key of its own and is never a principal, so its line in Workforce comes
 * from the DE it reports to. One short line, never draft text or a secret.
 */
export const WorkforceActivityReport = z
  .strictObject({
    /** The agent's slug (its Hermes profile id), e.g. `gitelman-copywriter`. */
    agent: z
      .string()
      .max(64)
      .regex(/^[a-z0-9][a-z0-9-]{0,63}$/),
    note: z.string().trim().min(1).max(500),
  })
  .meta({ id: "WorkforceActivityReport" });

export const WorkforceActivityReported = z
  .object({
    agent_id: Uuid,
    slug: z.string(),
    last_activity: z.string(),
    last_activity_at: Timestamp,
  })
  .meta({ id: "WorkforceActivityReported" });

export const workforceRoutes = {
  "workforceAgents.reportActivity": defineRoute({
    ...core,
    id: "workforceAgents.reportActivity",
    method: "post",
    path: `${base}/agent/workforce-activity`,
    tag: "workforce",
    summary:
      "A DE reports what one of its own agents (AG) is doing: the agent's last activity line (runs:write)",
    access: "run_token",
    params: CompanyParams,
    body: WorkforceActivityReport,
    response: { status: 200, schema: WorkforceActivityReported },
    errors: [],
    emits: ["workforce_agent.updated"],
  }),
  "workforceAgents.list": defineRoute({
    ...core,
    id: "workforceAgents.list",
    method: "get",
    path: `${base}/workforce/agents`,
    tag: "workforce",
    summary: "List the company's agents (AG), with team, whom each reports to and its runtime",
    access: "viewer",
    params: CompanyParams,
    query: WorkforceAgentListQuery,
    response: { status: 200, schema: page(WorkforceAgentSummary) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "workforceAgents.create": defineRoute({
    ...core,
    id: "workforceAgents.create",
    method: "post",
    path: `${base}/workforce/agents`,
    tag: "workforce",
    summary: "Add an agent (AG) by hand (a manager, or a person it will report to or stand above)",
    access: "member",
    params: CompanyParams,
    body: WorkforceAgentCreate,
    idempotency: "optional",
    response: { status: 201, schema: WorkforceAgentSummary },
    // ag_cap_reached: BILLING-PLANS, the DE is at its maintenance tier's AG cap
    errors: ["supervisor_required", "conflict", "ag_cap_reached", "agent_mode_managed"],
    emits: ["workforce_agent.registered"],
  }),
  "workforceAgents.get": defineRoute({
    ...core,
    id: "workforceAgents.get",
    method: "get",
    path: `${base}/workforce/agents/{id}`,
    tag: "workforce",
    summary: "An agent (AG)",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: WorkforceAgentSummary },
    errors: [],
    emits: [],
  }),
  "workforceAgents.update": defineRoute({
    ...core,
    id: "workforceAgents.update",
    method: "patch",
    path: `${base}/workforce/agents/{id}`,
    tag: "workforce",
    summary:
      "Pause, resume, retire, rename, move or reassign an agent (manager, the person it reports to, or a person above it)",
    access: "member",
    params: CompanyItemParams,
    body: WorkforceAgentPatch,
    response: { status: 200, schema: WorkforceAgentSummary },
    errors: ["supervisor_required", "ag_cap_reached"],
    emits: ["workforce_agent.updated"],
  }),
  // PURGE-RETIRED-AGENTS: a retired AG removed for good (owners and admins; never an agent).
  "workforceAgents.purgePlan": defineRoute({
    ...core,
    id: "workforceAgents.purgePlan",
    method: "get",
    path: `${base}/workforce/agents/{id}/purge-plan`,
    tag: "workforce",
    summary: "What removing a retired agent (AG) permanently deletes and keeps",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: AgentPurgePlan },
    errors: [],
    emits: [],
  }),
  "workforceAgents.purge": defineRoute({
    ...core,
    id: "workforceAgents.purge",
    method: "post",
    path: `${base}/workforce/agents/{id}/purge`,
    tag: "workforce",
    summary:
      "Remove a retired agent (AG) permanently, its name typed: its record, profile, helper links and endpoints deleted; history kept as 'Removed agent (name)'",
    access: "manager",
    params: CompanyItemParams,
    body: AgentPurge,
    response: { status: 200, schema: AgentPurgeResult },
    errors: ["validation_failed", "conflict"],
    emits: ["agent.purged"],
  }),
  "workforceAgents.addUse": defineRoute({
    ...core,
    id: "workforceAgents.addUse",
    method: "put",
    path: `${base}/workforce/agents/{id}/used-by/{membership_id}`,
    tag: "workforce",
    summary:
      "A DE uses this agent too: it shows under that DE as well as its home (manager, or whoever may change the agent)",
    access: "member",
    params: AgentUseParams,
    response: { status: 200, schema: WorkforceAgentSummary },
    errors: [],
    emits: ["workforce_agent.use_changed"],
  }),
  "workforceAgents.removeUse": defineRoute({
    ...core,
    id: "workforceAgents.removeUse",
    method: "delete",
    path: `${base}/workforce/agents/{id}/used-by/{membership_id}`,
    tag: "workforce",
    summary:
      "A DE no longer uses this agent (its home is unchanged; manager, or whoever may change the agent)",
    access: "member",
    params: AgentUseParams,
    response: { status: 200, schema: WorkforceAgentSummary },
    errors: [],
    emits: ["workforce_agent.use_changed"],
  }),
  "workforceAgents.declareUse": defineRoute({
    ...core,
    id: "workforceAgents.declareUse",
    method: "post",
    path: `${base}/agent/helpers-used`,
    tag: "workforce",
    summary:
      "A DE declares a helper (AG) it uses, or no longer uses, by the agent's slug (runs:write)",
    access: "run_token",
    params: CompanyParams,
    body: AgentUseDeclare,
    response: { status: 200, schema: AgentUseDeclared },
    errors: [],
    emits: ["workforce_agent.use_changed"],
  }),
  "workforce.activity": defineRoute({
    feature: "agents",
    id: "workforce.activity",
    method: "get",
    path: `${base}/workforce/activity`,
    tag: "workforce",
    summary:
      "What the digital workforce is doing: runs the caller can view, live first, with task, who, last line and waiting approval",
    access: "member",
    params: CompanyParams,
    query: ActivityQuery,
    response: { status: 200, schema: ActivityFeed },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "workforce.runtime": defineRoute({
    feature: "agents",
    id: "workforce.runtime",
    method: "get",
    path: `${base}/workforce/runtime`,
    tag: "workforce",
    summary:
      "The digital workforce's runs over a window: count, active time, tokens and cost (the runs the caller can view)",
    access: "member",
    params: CompanyParams,
    query: RuntimeQuery,
    response: { status: 200, schema: RuntimeSummary },
    errors: [],
    emits: [],
  }),
} as const;
